import { TypedDocumentNode, gql } from '@apollo/client';
import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { RivalsLogo } from '@sorare/core/src/atoms/icons/RivalsLogo';
import StarBall from '@sorare/core/src/atoms/icons/StarBall';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, DisplayM, LabelS } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import useLifecycle, { LIFECYCLE } from '@sorare/core/src/hooks/useLifecycle';
import { glossary } from '@sorare/core/src/lib/glossary';

import { GridBackground } from '@sorare/gameplay/src/components/rivals/GridBackground';

import { RivalsPlayerCard } from 'components/rivals/match/RivalsPlayerCard';
import { useHasRivalsLeagues } from 'hooks/useHasRivalsLeagues';

import {
  CountTowardsRivalsLeagueDialog_footballRivalsGame,
  CountTowardsRivalsLeagueDialog_so5Root,
} from './__generated__/index.graphql';

const EuropeanChampionship = styled.p`
  font-family: Druk Wide;
  font-size: 12px;
  text-transform: uppercase;
  color: var(--c-neutral-100);
`;

const StyledStarBall = styled(StarBall)`
  margin-top: -12px;
`;

const ZeroPercent = styled(LabelS)`
  align-self: center;
  border-radius: var(--intermediate-unit);
  border: 1px solid rgba(var(--c-rgb-neutral-1000), 0.5);
  padding: 2px var(--half-unit);
`;

const ContentWrapper = styled(Vertical)`
  padding: var(--quadruple-unit) var(--double-unit) var(--double-unit);
  height: 100%;
`;

const Lineup = styled(Horizontal)`
  & > * {
    flex: 1;
  }
`;

const Content = styled(Vertical)`
  flex: 1;
  justify-content: center;
`;

const StyledGridBackground = styled(GridBackground)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;
const Header = styled(SBHorizontal)`
  background-color: var(--c-rivals-purple);
  clip-path: path('M433 45L169 54L174 60L-44 71L-123 -20H6008L600 40Z');
  content: '';
  padding: var(--double-unit);
  align-items: flex-start;
`;

const ButtonWrapper = styled.div`
  display: flex;
  width: 100%;
  padding: 0 var(--triple-unit);
`;

const RadialGradientBg = styled.div`
  background: #ad00ff;
  position: absolute;
  inset: -20% -20% 0;
  filter: blur(100px);
  z-index: -2;
  border-radius: 50%;
  aspect-ratio: 1;
`;

type Props = {
  footballRivalsGame: Nullable<CountTowardsRivalsLeagueDialog_footballRivalsGame>;
  so5Root: Nullable<CountTowardsRivalsLeagueDialog_so5Root>;
};

export const CountTowardsRivalsLeagueDialog = ({
  footballRivalsGame,
  so5Root,
}: Props) => {
  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const { hasLeagues } = useHasRivalsLeagues(so5Root);

  if (
    footballRivalsGame?.userGroupTournaments.length === 0 ||
    !hasLeagues ||
    lifecycle?.[LIFECYCLE.sawRivalsLeagueCrossMode]
  ) {
    return null;
  }
  const hasBlockchainCard = footballRivalsGame?.myLineup?.appearances.some(
    appearance => appearance.rarity !== 'common'
  );
  return (
    <Dialog
      open
      hideHeader
      maxWidth="xs"
      fullScreen={false}
      onClose={() => {
        updateLifecycle(LIFECYCLE.sawRivalsLeagueCrossMode, true);
      }}
    >
      <StyledGridBackground
        height="100%"
        color="#aeaeae"
        customGradient="radial-gradient(transparent 0%, var(--c-neutral-100) 120%),
          linear-gradient(
            180deg,
            rgba(var(--c-rgb-neutral-100), 0) 0%,
            var(--c-neutral-100) 100%
          )"
      >
        <RadialGradientBg />
        <Header>
          <RivalsLogo width={62} color="var(--c-neutral-100)" />
          <EuropeanChampionship>
            <FormattedMessage {...glossary.europeanChampionship} />
            <StyledStarBall color="var(--c-neutral-100)" width={10} />
          </EuropeanChampionship>
        </Header>

        <ContentWrapper center gap={6}>
          <Lineup>
            {footballRivalsGame?.myLineup?.appearances.map(appearance => (
              <Vertical key={appearance.id}>
                <RivalsPlayerCard
                  key={appearance.id}
                  appearance={appearance}
                  width={160}
                />
                {appearance.rarity !== 'common' && (
                  <ZeroPercent>0%</ZeroPercent>
                )}
              </Vertical>
            ))}
          </Lineup>
          <Content>
            <DisplayM>
              <FormattedMessage
                id="CountTowardsRivalsLeagueDialog.title"
                defaultMessage="Rivals<br></br>Leagues"
                values={{
                  br: () => <br />,
                }}
              />{' '}
              <FontAwesomeIcon
                icon={faCheck}
                size="1x"
                color="var(--c-rivals-purple)"
              />
            </DisplayM>
            <BodyM>
              {hasBlockchainCard ? (
                <FormattedMessage
                  id="CountTowardsRivalsLeagueDialog.blockchainCardDescription"
                  defaultMessage="Your lineup counts towards your Rivals Leagues, but scarcity bonuses do not apply."
                />
              ) : (
                <FormattedMessage
                  id="CountTowardsRivalsLeagueDialog.descriptionWithoutBlockchainCard"
                  defaultMessage="Your lineup also counts towards your Rivals Leagues."
                />
              )}
            </BodyM>
          </Content>
          <ButtonWrapper>
            <Button
              size="medium"
              onClick={() => {
                updateLifecycle(LIFECYCLE.sawRivalsLeagueCrossMode, true);
              }}
              color="secondary"
              fullWidth
            >
              <FormattedMessage
                id="CountTowardsRivalsLeagueDialog.cta"
                defaultMessage="Got it"
              />
            </Button>
          </ButtonWrapper>
        </ContentWrapper>
      </StyledGridBackground>
    </Dialog>
  );
};

CountTowardsRivalsLeagueDialog.fragments = {
  footballRivalsGame: gql`
    fragment CountTowardsRivalsLeagueDialog_footballRivalsGame on FootballRivalsGame {
      slug
      myLineup {
        id
        appearances {
          id
          rarity
          ...RivalsPlayerCard_footballRivalsAppearance
        }
      }
      userGroupTournaments {
        id
      }
    }
    ${RivalsPlayerCard.fragments.footballRivalsAppearance}
  ` as TypedDocumentNode<CountTowardsRivalsLeagueDialog_footballRivalsGame>,
  so5Root: gql`
    fragment CountTowardsRivalsLeagueDialog_so5Root on So5Root {
      id
      ...useHasRivalsLeagues_so5Root
    }
    ${useHasRivalsLeagues.fragments.so5Root}
  ` as TypedDocumentNode<CountTowardsRivalsLeagueDialog_so5Root>,
};
